/**
 * Сборка данных плитки «Популярных» из карточки бэка: цена (с учётом settings, акции и
 * этапов тренда), рейтинг/использования модели и ссылка на КОНТЕКСТНЫЙ лендинг.
 *
 * Вынесено из PopularModelsTab (12.09), чтобы блоки-ленты раздела (FeedBlocks) показывали
 * ТЕ ЖЕ карточки с ТОЙ ЖЕ ценой, что сетка страницы: цена на витрине обязана совпадать с
 * тем, что спишет бэк, поэтому формула должна быть одна на оба места, а не скопирована.
 */
import type { Model } from '../../../data/models'
import { cardCtxUrl, type ToolPage } from './cardSeo.ts'
import { effectiveCardCover, trendGroupFields, type BackendCard, type PopularCardItem } from './popularCardApply.ts'
import { cardInputVideoContext, computeModelCost } from '../../../utils/modelCost'
import { activeDiscount, trendDiscount, trendTotalCost } from '../../../utils/discount'

export interface PopularCardView {
	item: PopularCardItem
	/** Цена карточки «как спишет бэк» — realCost плитки (скрывается при show_stats=false). */
	cost: number
	rating: number | undefined
	users: number | undefined
}

export function buildPopularCardView(
	card: BackendCard,
	index: number,
	opts: {
		findModel: (adapter: string, modelId: string) => Model | null
		category: ToolPage
		/** Активный пебл витрины — чтобы клик не выбивал юзера из его категории. */
		landingCtx?: string | null
	},
): PopularCardView {
	const { findModel, category, landingCtx = null } = opts
	const realModel =
		card.target_adapter && card.target_model
			? findModel(card.target_adapter, card.target_model)
			: null
	// Цена с учётом admin-заданных settings карточки (resolution/
	// duration/...). Без этого realModel.cost = min_credits_price
	// (минимум по variants). См. utils/modelCost.ts — та же функция
	// в useGenerateCost, чтобы цены на карточке и на /generate совпадали.
	// «Своя цена карточки»: без акции — floor-цена (max с оверрайдом),
	// ровно то, что спишет бэк. На время акции цена = промо КАК ЕСТЬ:
	// скидки-уровнями промо честно НИЖЕ базы (бэк спишет её же —
	// effective_source_price; гейты пройдены при назначении в админке).
	let discount = activeDiscount(
		card.price_override,
		card.discount_price,
		card.discount_until,
	)
	// Гейт промо (зеркало бэка effective_source_price): скидка
	// действует на карточку с ЕЁ пресетом. Параметры дороже
	// обычной цены (админ сменил их после назначения акции) →
	// бэк спишет расчёт, значит и витрина показывает расчёт.
	const modelCost = computeModelCost(realModel, card.settings, cardInputVideoContext(realModel, card))
	const cardBase = card.price_override ?? modelCost
	if (discount && modelCost > cardBase) discount = null
	let cardCost = discount
		? discount.price
		: Math.max(modelCost, card.price_override ?? 0)
	// Обычная цена ДО акции — зачёркнутый ценник промо-стикера.
	let cardRegular = discount ? Math.max(modelCost, card.price_override ?? 0) : 0
	// Тренд: цена «под ключ» = сумма цен этапов, акция — из
	// этапов (−N% до даты); формула промо = списание бэка.
	// По pipeline.enabled, НЕ по слагу (см. HomeTemp).
	if (card.pipeline?.enabled) {
		discount = trendDiscount(card.pipeline)
		cardCost = trendTotalCost(card.pipeline)
		cardRegular = discount
			? (card.pipeline.photo?.credits || 0) + (card.pipeline.video?.credits || 0)
			: 0
	}
	const item: PopularCardItem = {
		id: card.id || `card-${index}`,
		title: card.title || '',
		description: card.description || '',
		backgroundImageUrl: effectiveCardCover(card),
		backgroundPreviewUrl: card.background_preview_url || null,
		tileStyle: card.tile_style || undefined,
		modelLogoUrl: realModel?.icon || '',
		creditsLabel: realModel || (card.pipeline?.enabled && cardCost > 0) ? String(cardCost) : '',
		rating: realModel?.rating || 0,
		reviews: realModel?.users || 0,
		targetAdapter: card.target_adapter || '',
		targetModelId: card.target_model || '',
		defaultPrompt: card.system_prompt || '',
		settings: card.settings,
		usedCount: card.used_count,
		videoUrl: card.video_url,
		videoLoopUrl: card.video_loop_url || undefined,
		audioUrl: card.audio_url,
		// Пайплайн-карточка (тренд): жива без target_adapter —
		// клик откроет многоэтапный GTM по slug'у.
		// Карточка-флоу без модели — норма: клик ведёт в шаблон; у группы трендов применяется участник.
		disabled: !card.target_adapter && !card.pipeline_slug && card.card_kind !== 'flow' && card.card_kind !== 'multitrend',
		pipelineSlug: card.pipeline_slug,
		demoType: card.demo_type,
		demoBeforeUrl: card.demo_before_url,
		demoAfterUrl: card.demo_after_url,
		demoVariants: card.demo_variants,
		cardKind: card.card_kind,
		...trendGroupFields(card),
		flowTemplateId: card.flow_template_id,
		flowSteps: card.flow?.steps,
		flowPriceFrom: card.flow?.price_from,
		flowCoverPosition: card.flow?.cover_position ?? null,
		userInput: card.user_input,
		photoSlots: card.photo_slots,
		hideCharacter: card.hide_character,
		userPhotoLimit: card.user_photo_limit,
		userPhotoHint: card.user_photo_hint,
		hideEndImage: card.hide_end_image,
		endImageHint: card.end_image_hint,
		characterHint: card.character_hint,
		promptLocked: card.prompt_locked,
		referenceImageUrl: card.reference_image_url,
							userImageLocked: card.user_image_locked,
							referenceVideoUrl: card.reference_video_url,
							hasRefVideo: card.has_reference_video,
							hasRefAudio: card.has_reference_audio,
							refVideoSeconds: card.reference_video_seconds,
		userVideoLocked: card.user_video_locked,
		referenceAudioUrl: card.reference_audio_url,
		userAudioLocked: card.user_audio_locked,
		priceOverride: discount?.price ?? card.price_override,
		discountPercent: discount?.percent,
		discountLeftLabel: discount?.leftLabel,
		discountUntil: discount?.until,
		creditsOldLabel: discount && cardRegular > cardCost ? String(cardRegular) : undefined,
		regularPrice: discount && cardRegular > 0 ? cardRegular : undefined,
		slug: card.slug,
		// СТРАНИЧНЫЙ лендинг (не primary!): клик/href держат юзера в
		// текущем разделе — карточка на двух лейнах не прыгает на
		// другую страницу (canonical у лендинга свой).
		primaryUrl: cardCtxUrl(card, category, landingCtx),
	}
	return { item, cost: cardCost, rating: realModel?.rating, users: realModel?.users }
}
